/**
 * Shared message types for preload bridge communication.
 * This file can be imported by both frontend and service worker.
 */

import { PreloadState, PreloadStatusMessage } from './PreloadTypes';

/**
 * Message types for preload bridge communication
 */
export enum PreloadBridgeMessageType {
    // Input messages (from frontend to service worker)
    getStatus = 'preload-get-status',
    startPreload = 'preload-start',
    forceRefresh = 'preload-force-refresh',
    ensureBundles = 'preload-ensure-bundles',
    activateForSchool = 'preload-activate-for-school',

    // Output messages (from service worker to frontend)
    status = 'preload-status'
}

/**
 * Base class for bridge input messages (frontend to service worker)
 */
export class PreloadBridgeInputMessage {
    type: string = '';
}

/**
 * Input message: Request current preload status
 */
export class GetPreloadStatusMessage extends PreloadBridgeInputMessage {
    type = PreloadBridgeMessageType.getStatus;
}

/**
 * Input message: Start/resume preload process
 */
export class StartPreloadMessage extends PreloadBridgeInputMessage {
    type = PreloadBridgeMessageType.startPreload;
}

/**
 * Input message: Force refresh all bundles
 */
export class ForceRefreshPreloadMessage extends PreloadBridgeInputMessage {
    type = PreloadBridgeMessageType.forceRefresh;
}

/**
 * Input message: Ensure named optional bundles are downloaded.
 *
 * Used by the frontend to request targeted bundles (e.g. 'fonts-special')
 * when it determines that a specific set of assets is needed — for example
 * when an assessment's language requires non-Latin fonts that are not in
 * the always-on core bundle.
 */
export class EnsurePreloadBundlesMessage extends PreloadBridgeInputMessage {
    type = PreloadBridgeMessageType.ensureBundles;
    bundleNames: string[];

    constructor(bundleNames: string[]) {
        super();
        this.bundleNames = bundleNames;
    }
}

/**
 * Input message: Activate preload for a Google Admin-managed school code.
 * Sent by the frontend after reading primarySchoolCode from the Chrome OS
 * managed configuration API. The school code is pre-normalized to a number
 * (leading zeros stripped via parseInt). The service worker checks it
 * against manifest.schoolCodeAllowlist.
 */
export class ActivateForSchoolMessage extends PreloadBridgeInputMessage {
    type = PreloadBridgeMessageType.activateForSchool;
    schoolCode: number;

    constructor(schoolCode: number) {
        super();
        this.schoolCode = schoolCode;
    }
}

// Re-export types for convenience
export { PreloadState, PreloadStatusMessage };
